// File: frontend/src/pages/Socrates/Socrates.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { SocratesProvider, useSocrates } from './contexts/SocratesContext';
import { TemplateProvider } from './contexts/TemplateContext';
import { ParameterProvider } from './contexts/ParameterContext';
import { AIAgentsProvider } from './contexts/AIAgentsContext';
import Dashboard from './components/Dashboard';
import TemplateManagement from './components/TemplateManagement';
import AIAgents from './components/AIAgents';
import AIAgentsAdmin from './components/AIAgentsAdmin';
import styles from './Socrates.module.css';

const SocratesContent = () => {
  const { t } = useLanguage();
  const { isSuperAdmin, userData, loading: socratesLoading } = useSocrates();
  const location = useLocation();
  const navigate = useNavigate();
  const { plato_agent_name } = useParams();
  const [activeSection, setActiveSection] = useState('dashboard');

  // Set active section based on URL path
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/socrates') {
      setActiveSection('dashboard');
    } else if (path.includes('/socrates/templates')) {
      setActiveSection('templates');
    } else if (path.includes('/socrates/ai-agents-admin')) {
      setActiveSection('ai-agents-admin');
    } else if (path.includes('/socrates/ai-agents')) {
      setActiveSection('ai-agents');
    }
  }, [location.pathname]);

  // Handle navigation between sections
  const handleNavigation = (section) => {
    setActiveSection(section);
    
    switch (section) {
      case 'dashboard':
        navigate('/socrates');
        break;
      case 'templates':
        navigate('/socrates/templates');
        break;
      case 'ai-agents':
        navigate('/socrates/ai-agents');
        break;
      case 'ai-agents-admin':
        navigate('/socrates/ai-agents-admin');
        break;
      default:
        navigate('/socrates');
    }
  };

  // Debug logging
  useEffect(() => {
    console.log('Socrates Debug:', {
      userData,
      isSuperAdmin,
      userRole: userData?.role_key,
      socratesLoading
    });
  }, [userData, isSuperAdmin, socratesLoading]);

  // Render the active section
  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigation} />;
      case 'templates':
        return <TemplateManagement />;
      case 'ai-agents':
        return <AIAgents agentName={plato_agent_name} />;
      case 'ai-agents-admin':
        return isSuperAdmin ? (
          <AIAgentsAdmin agentName={plato_agent_name} />
        ) : (
          <div className={styles.accessDenied}>
            <h2>{t('access_denied')}</h2>
            <p>{t('super_admin_required')}</p>
            <p>Debug: isSuperAdmin = {String(isSuperAdmin)}, role = {userData?.role_key}</p>
          </div>
        );
      default:
        return <Dashboard onNavigate={handleNavigation} />;
    }
  };

  return (
    <ParameterProvider>
      <TemplateProvider>
        <AIAgentsProvider>
          <div className={styles.socratesPage}>            
            <div className={styles.navigation}>
              <button 
                className={`${styles.navButton} ${activeSection === 'dashboard' ? styles.active : ''}`}
                onClick={() => handleNavigation('dashboard')}
              >
                {t('dashboard')}
              </button>
              <button 
                className={`${styles.navButton} ${activeSection === 'templates' ? styles.active : ''}`}
                onClick={() => handleNavigation('templates')}
              >
                {t('template_management')}
              </button>
              <button 
                className={`${styles.navButton} ${activeSection === 'ai-agents' ? styles.active : ''}`}
                onClick={() => handleNavigation('ai-agents')}
              >
                {t('ai_agents')}
              </button>
              {isSuperAdmin && (
                <button 
                  className={`${styles.navButton} ${activeSection === 'ai-agents-admin' ? styles.active : ''}`}
                  onClick={() => handleNavigation('ai-agents-admin')}
                >
                  {t('ai_agents_admin')}
                </button>
              )}
            </div>
            
            <div className={styles.sectionContent}>
              {renderActiveSection()}
            </div>
          </div>
        </AIAgentsProvider>
      </TemplateProvider>
    </ParameterProvider>
  );
};

const Socrates = () => {
  return (
    <SocratesProvider>
      <SocratesContent />
    </SocratesProvider>
  );
};

export default Socrates;